import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, CheckCircle2, XCircle, Loader2, Shield, Zap, Award,
  Code2, HeartPulse, Factory, Briefcase, Landmark, GraduationCap, Building2,
  Cpu, Lock, Search, MessagesSquare, Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ---------- palette (inline so it can't get overridden by shadcn defaults) ---------- */
const NAVY = "#0A0F1E";
const NAVY_95 = "rgba(10,15,30,0.92)";
const BLUE = "#2563EB";
const INK = "#0F172A";
const SLATE = "#64748B";
const OFF_WHITE = "#F7F8FC";
const PURPLE = "#7C3AED";

/* ---------- data ---------- */
const STATS = [
  { v: "42%", k: "of critical knowledge exists only in one person's head" },
  { v: "6–9 months", k: "for a replacement to reach full productivity" },
  { v: "213 days", k: "average time to fill a senior role" },
  { v: "$30,000+", k: "true cost of replacing a mid-level employee" },
];

const STEPS = [
  { icon: Cpu, title: "Capture",
    body: "Ragnes works silently in the background. It captures decisions made in Slack, emails sent in Gmail, code committed in GitHub, files edited on the desktop. No manual input. No behavior change required." },
  { icon: Lock, title: "Preserve",
    body: "Every piece of knowledge is privacy-screened before storage. Passwords, financial data, personal messages, and Aadhaar/PAN numbers are blocked at source — they never reach our servers. What's captured is purely professional work context." },
  { icon: MessagesSquare, title: "Query forever",
    body: "Type /alex and ask anything. What was her vendor strategy? What's unfinished? Who are the key contacts? Get cited answers with a confidence score. Works in the web app, on WhatsApp, or via API." },
];

const INDUSTRY_ICON = {
  "Information Technology": Code2,
  "Healthcare": HeartPulse,
  "Manufacturing & Operations": Factory,
  "Professional Services & Legal": Briefcase,
  "Banking & Financial Services": Landmark,
  "Education & Universities": GraduationCap,
  "Government & Public Sector": Building2,
};
const INDUSTRIES_COPY = {
  "Information Technology": "When a senior engineer or architect leaves, their codebase decisions, system design rationale, and vendor relationships don't disappear. Junior engineers query their bot to understand why systems were built a certain way.",
  "Healthcare": "Query patient history, medication records, and treatment decisions by registration number. When a doctor transfers wards, the next physician starts fully informed. HIPAA and DPDP compliant.",
  "Manufacturing & Operations": "Every machine has a maintenance history. Every engineer has a workaround. When your senior technician retires, their 20 years of floor knowledge lives on in their bot.",
  "Professional Services & Legal": "Client relationships, case history, negotiation patterns, billing context — preserved when partners retire or associates move firms. Your institutional memory stays with the firm.",
  "Banking & Financial Services": "Relationship managers hold years of client context. When they move to competitors, that context doesn't have to go with them. Compliance-ready with full audit trails.",
  "Education & Universities": "When a tenured professor retires, their teaching philosophy, student interventions, research contacts, and curriculum decisions remain accessible to their successor.",
  "Government & Public Sector": "Institutional memory in government is a national asset. Ragnes ensures policy knowledge, stakeholder relationships, and process expertise survive election cycles and retirements.",
};

const CAPTURED = [
  "Decisions made in Slack channels",
  "Work emails you sent (summarised, never stored raw)",
  "Git commits and code review comments",
  "Files you created or edited",
  "Meeting decisions and action items",
  "Client and stakeholder notes",
];
const NEVER = [
  "Credit card or bank account numbers",
  "Aadhaar, PAN, passport, or SSN numbers",
  "WhatsApp or personal messages",
  "Passwords, API keys, or tokens",
  "Private keys or certificates",
  "Personal health or financial records",
];

/* Pricing tier definitions — shared with /pricing page. Flat monthly prices (not per-user). */
export const PRICING_TIERS = [
  {
    id: "starter", name: "Starter", cap: "Up to 10 users",
    monthly: { USD: 20, INR: 299 }, annual: { USD: 17, INR: 249 },
    annualBilled: { USD: 204, INR: 2988 },
    features: [
      "Core memory + Individual Memory",
      "/alias chat with citations",
      "Handover pack (PDF + JSON)",
      "5 integrations (Slack, Gmail, Drive, GitHub, HubSpot)",
      "Community support",
    ],
    cta: "Get started",
  },
  {
    id: "growth", name: "Growth", cap: "Up to 50 users", featured: true,
    monthly: { USD: 30, INR: 499 }, annual: { USD: 25, INR: 415 },
    annualBilled: { USD: 300, INR: 4980 },
    features: [
      "Everything in Starter",
      "Employee Induction System",
      "Bot analytics dashboard",
      "WhatsApp Business bot",
      "Priority support (24h response)",
    ],
    cta: "Get started",
  },
  {
    id: "enterprise", name: "Enterprise", cap: "Unlimited users",
    monthly: { USD: null, INR: null }, annual: { USD: null, INR: null },
    features: [
      "Everything in Growth",
      "DPDP & HIPAA compliance mode",
      "On-premise deployment",
      "Dedicated Customer Success Manager",
      "99.9% uptime SLA + custom contracts",
    ],
    cta: "Contact us",
  },
];

export const WI_ADDON = {
  monthly: { USD: 5, INR: 149 }, annual: { USD: 4, INR: 119 },
};

export function formatPrice(v, currency) {
  if (v == null) return "Custom";
  return currency === "INR" ? `₹${v.toLocaleString("en-IN")}` : `$${v.toLocaleString("en-US")}`;
}

/* ---------- geo currency hook ---------- */
export function useCurrency() {
  const [state, setState] = useState({ currency: "USD", country: null, source: "loading" });
  useEffect(() => {
    const forced = localStorage.getItem("eb_currency");
    if (forced === "USD" || forced === "INR") {
      setState({ currency: forced, country: null, source: "manual" });
      return;
    }
    fetch(`${BACKEND_URL}/api/geo`).then((r) => r.json()).then((d) => {
      setState({ currency: d.currency, country: d.country, source: d.source });
    }).catch(() => setState({ currency: "USD", country: null, source: "fallback" }));
  }, []);
  const setCurrency = (c) => {
    localStorage.setItem("eb_currency", c);
    setState((s) => ({ ...s, currency: c, source: "manual" }));
  };
  return { ...state, setCurrency };
}

/* ---------- component ---------- */
export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [industries, setIndustries] = useState({ industries: [], company_sizes: [] });

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on(); window.addEventListener("scroll", on);
    fetch(`${BACKEND_URL}/api/early-access/industries`).then((r) => r.json()).then(setIndustries).catch(() => {});
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { window.removeEventListener("scroll", on); document.documentElement.style.scrollBehavior = ""; };
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen font-sans" style={{ background: "#fff", color: INK }} data-testid="landing-page-container">
      {/* ============ NAV ============ */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all"
        style={{ background: scrolled ? NAVY_95 : "transparent", backdropFilter: scrolled ? "blur(14px)" : "none",
                 borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent" }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between text-white">
          <Link to="/" className="font-bold text-lg tracking-tight flex items-center gap-2" data-testid="landing-brand-link">
            <Sparkles className="w-4 h-4" style={{ color: BLUE }} />
            Ragnes
          </Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm">
            <button onClick={() => scrollTo("industries")} className="hover:text-white/90 text-white/80">Product</button>
            <button onClick={() => scrollTo("industries")} className="hover:text-white/90 text-white/80">Industries</button>
            <Link to="/pricing" className="hover:text-white/90 text-white/80" data-testid="nav-pricing-link">Pricing</Link>
            <button onClick={() => scrollTo("privacy")} className="hover:text-white/90 text-white/80">Privacy</button>
            <button onClick={() => scrollTo("early-access")} className="hover:text-white/90 text-white/80">Early Access</button>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-white/80 hover:text-white hidden sm:inline">Sign in</Link>
            <button onClick={() => scrollTo("early-access")} data-testid="nav-early-access-button"
              className="text-sm font-medium text-white rounded-full px-4 py-2 transition-transform hover:scale-[1.03]"
              style={{ background: BLUE }}>
              Get early access →
            </button>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden" style={{ background: NAVY }} data-testid="landing-hero">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 0%, rgba(37,99,235,0.25), transparent 40%), radial-gradient(circle at 80% 30%, rgba(124,58,237,0.18), transparent 45%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center text-white">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs text-white/90" data-testid="landing-hero-chip">
              <Shield className="w-3 h-3" style={{ color: BLUE }} /> Trusted by Entrepreneurs in 30+ Countries
            </span>
            <h1 className="mt-6 font-bold tracking-tight" style={{ fontSize: "clamp(36px,5.5vw,56px)", lineHeight: 1.05 }}>
              Your employees leave.<br />Their knowledge doesn't have to.
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-xl leading-relaxed">
              Ragnes captures every decision, process, and relationship your team builds — and makes it queryable forever. When someone leaves, their replacement starts on Day 1 with everything.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button onClick={() => scrollTo("early-access")} data-testid="hero-early-access-cta"
                className="rounded-full px-6 h-12 text-sm font-medium text-white transition-transform hover:scale-[1.03]"
                style={{ background: BLUE }}>
                Request early access →
              </button>
              <button onClick={() => scrollTo("how")} data-testid="hero-demo-cta"
                className="rounded-full px-6 h-12 text-sm font-medium text-white border border-white/30 hover:bg-white/5">
                See it in action
              </button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs text-white/70 font-medium uppercase tracking-wider">
              <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" style={{ color: BLUE }} /> Privacy-first</span>
              <span className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5" style={{ color: PURPLE }} /> DPDP &amp; HIPAA ready</span>
              <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" style={{ color: "#10B981" }} /> Live in 24 hours</span>
            </div>
          </div>

          {/* HERO VISUAL — chat mock */}
          <div className="relative">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 shadow-2xl" data-testid="landing-hero-mock">
              <div className="rounded-lg bg-slate-800/70 px-4 py-3 text-sm text-slate-200 font-mono">
                <span style={{ color: BLUE }}>/alex</span> what was her approach to the APAC client negotiations?
              </div>
              <div className="mt-4 rounded-xl bg-white/95 text-slate-800 p-5 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold" style={{ background: PURPLE }}>A</div>
                  <div>
                    <p className="text-xs font-medium">Alex's memory</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">preserved · read-only</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-slate-700">
                  Alex built relationships 6 months before contract renewal, always involving the CFO early.
                  Key contacts: <span className="font-medium">Rajiv Mehta</span> (CFO, Tata Steel),
                  <span className="font-medium"> Sandra Lee</span> (Procurement, Singapore)…
                </p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium" style={{ background: "#DCFCE7", color: "#065F46" }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> 94% confidence · 23 memory entries · 4 sources cited
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROBLEM ============ */}
      <section className="py-20 lg:py-28" style={{ background: "#fff" }} data-testid="landing-problem">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,44px)", color: INK }}>
            The invisible cost of every departure
          </h2>
          <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: SLATE }}>
            Most companies don't realize how much knowledge walks out the door — until it's too late.
          </p>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.v} className="rounded-2xl p-6 text-left border" style={{ borderColor: "#E2E8F0", background: OFF_WHITE }} data-testid={`landing-stat-${s.v.replace(/\W+/g, "-")}`}>
                <p className="text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: BLUE }}>{s.v}</p>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: SLATE }}>{s.k}</p>
              </div>
            ))}
          </div>
          <blockquote className="mt-16 italic text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto" style={{ color: INK }}>
            “We lost 11 years of client relationship knowledge when our VP left. We didn't even know what we didn't know.”
            <footer className="mt-4 text-sm not-italic font-medium" style={{ color: SLATE }}>
              — Anonymous Fortune 500 CHRO
            </footer>
          </blockquote>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="py-20 lg:py-28" style={{ background: OFF_WHITE }} data-testid="landing-how-it-works">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,44px)", color: INK }}>
              Three steps to never losing knowledge again
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-2xl bg-white border p-7" style={{ borderColor: "#E2E8F0" }} data-testid={`landing-step-${i}`}>
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white" style={{ background: BLUE }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="mt-5 text-xs uppercase tracking-wider font-semibold" style={{ color: BLUE }}>Step 0{i + 1}</p>
                  <h3 className="mt-1 text-xl font-semibold" style={{ color: INK }}>{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: SLATE }}>{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ INDUSTRIES ============ */}
      <section id="industries" className="py-20 lg:py-28" style={{ background: "#fff" }} data-testid="landing-industries">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,44px)", color: INK }}>
              Built for every industry where knowledge is competitive advantage
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Object.entries(INDUSTRIES_COPY).map(([name, copy], i) => {
              const Icon = INDUSTRY_ICON[name] || Building2;
              return (
                <div key={name} className="rounded-2xl border p-7 transition-shadow hover:shadow-md" style={{ borderColor: "#E2E8F0" }} data-testid={`landing-industry-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#EEF2FF", color: BLUE }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold" style={{ color: INK }}>{name}</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: SLATE }}>{copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ PRIVACY ============ */}
      <section id="privacy" className="py-20 lg:py-28" style={{ background: OFF_WHITE }} data-testid="landing-privacy">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium" style={{ background: "#EDE9FE", color: PURPLE }}>
              <Shield className="w-3.5 h-3.5" /> Privacy foundation
            </span>
            <h2 className="mt-4 font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,44px)", color: INK }}>
              Built different. Privacy isn't a feature — it's the foundation.
            </h2>
            <p className="mt-4 text-lg" style={{ color: SLATE }}>
              We capture your professional work. We never touch anything personal.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white border p-7" style={{ borderColor: "#E2E8F0" }} data-testid="landing-privacy-captured">
              <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#10B981" }}>What Ragnes captures</p>
              <ul className="mt-5 space-y-3">
                {CAPTURED.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm" style={{ color: INK }}>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-white border p-7" style={{ borderColor: "#E2E8F0" }} data-testid="landing-privacy-never">
              <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#DC2626" }}>What Ragnes will NEVER read</p>
              <ul className="mt-5 space-y-3">
                {NEVER.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm" style={{ color: INK }}>
                    <XCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#DC2626" }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 rounded-2xl p-6 border" style={{ background: "#F5F3FF", borderColor: "#DDD6FE" }} data-testid="landing-privacy-callout">
            <p className="text-sm leading-relaxed" style={{ color: INK }}>
              <span className="font-semibold" style={{ color: PURPLE }}>Hard-block filters, always on.</span>{" "}
              Every piece of content passes through 20 hard-block privacy filters before it ever leaves your device.
              These filters cannot be turned off — not even by your admin.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {["DPDP Act 2023 Ready", "HIPAA Compliant Architecture"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium bg-white" style={{ borderColor: "#E2E8F0", color: INK }}>
                <Award className="w-3.5 h-3.5" style={{ color: PURPLE }} /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING TEASER ============ */}
      <PricingTeaser />

      {/* ============ EARLY ACCESS ============ */}
      <EarlyAccessForm industries={industries.industries} companySizes={industries.company_sizes} />

      {/* ============ FOOTER ============ */}
      <footer className="pt-14 pb-8" style={{ background: NAVY }} data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-white">
          <div>
            <p className="font-bold text-lg flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: BLUE }} /> Ragnes
            </p>
            <p className="mt-3 text-sm text-white/60 max-w-xs leading-relaxed">
              Preserve every decision, process, and relationship — even when your people move on.
            </p>
          </div>
          <div className="flex flex-wrap items-start gap-x-8 gap-y-2 text-sm text-white/70">
            <button onClick={() => scrollTo("industries")} className="hover:text-white">Product</button>
            <button onClick={() => scrollTo("industries")} className="hover:text-white">Industries</button>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <button onClick={() => scrollTo("privacy")} className="hover:text-white">Privacy</button>
            <Link to="/login" className="hover:text-white">Sign in</Link>
          </div>
          <p className="text-sm text-white/60 md:text-right">© 2026 Ragnes.io — Never lose institutional knowledge again.</p>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-6 text-xs text-white/50">
          <a href="#" className="hover:text-white/80">Privacy Policy</a>
          <a href="#" className="hover:text-white/80">Terms of Service</a>
          <a href="#" className="hover:text-white/80">Contact</a>
          <a href="#" className="hover:text-white/80">DPDP Compliance Statement</a>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Pricing teaser (compact, links to /pricing) ---------- */
function PricingTeaser() {
  const { currency } = useCurrency();
  return (
    <section className="py-20 lg:py-28" style={{ background: "#fff" }} data-testid="landing-pricing-teaser">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,44px)", color: INK }}>
            Simple pricing. Every currency. No surprises.
          </h2>
          <p className="mt-4 text-lg" style={{ color: SLATE }}>
            Choose the plan that fits your team. Prices auto-adjust to your region.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {PRICING_TIERS.map((t) => (
            <div key={t.id}
              className={`rounded-2xl p-7 border ${t.featured ? "text-white shadow-xl" : "bg-white"}`}
              style={t.featured
                ? { background: BLUE, borderColor: BLUE }
                : t.id === "enterprise"
                  ? { background: NAVY, borderColor: NAVY, color: "#fff" }
                  : { borderColor: "#E2E8F0" }}
              data-testid={`landing-plan-${t.id}`}>
              {t.featured && (
                <span className="inline-block mb-3 text-[10px] uppercase tracking-wider font-semibold rounded-full bg-white/25 px-2 py-0.5">Most popular</span>
              )}
              <p className="text-sm font-semibold" style={{ color: t.featured || t.id === "enterprise" ? "rgba(255,255,255,0.85)" : SLATE }}>
                {t.name}
              </p>
              <p className="mt-2 text-4xl font-bold">
                {formatPrice(t.monthly[currency], currency)}
                {t.monthly[currency] != null && <span className="text-base font-normal" style={{ color: t.featured || t.id === "enterprise" ? "rgba(255,255,255,0.7)" : SLATE }}>/month</span>}
              </p>
              <p className="mt-1 text-xs" style={{ color: t.featured || t.id === "enterprise" ? "rgba(255,255,255,0.7)" : SLATE }}>{t.cap}</p>
              <Link to="/pricing" className="mt-6 inline-flex items-center justify-center rounded-full w-full h-10 text-sm font-medium"
                style={t.featured ? { background: "#fff", color: BLUE } : t.id === "enterprise" ? { background: BLUE, color: "#fff" } : { background: INK, color: "#fff" }}
                data-testid={`landing-plan-cta-${t.id}`}>
                See full pricing
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Early Access ---------- */
function EarlyAccessForm({ industries, companySizes }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", industry: "", company_size: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const sizes = companySizes?.length ? companySizes : ["1-10", "11-50", "51-200", "201-500", "500+"];

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/early-access`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not join the waitlist. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally { setSubmitting(false); }
  };

  const inputCls = "h-11 w-full rounded-lg border px-3 text-sm text-white placeholder:text-white/40 outline-none focus:ring-2";

  return (
    <section id="early-access" className="py-20 lg:py-28" style={{ background: NAVY }} data-testid="landing-early-access">
      <div className="max-w-4xl mx-auto px-6 text-white">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-bold tracking-tight" style={{ fontSize: "clamp(28px,3.6vw,44px)" }}>
            Join 100 companies preserving their institutional knowledge
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Early access members get 6 months free. We onboard in batches of 10.
          </p>
        </div>

        <div className="mt-10 rounded-2xl p-6 lg:p-8 border" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}>
          {submitted ? (
            <div className="text-center py-10" data-testid="early-access-success-message">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "#10B981" }} />
              <p className="text-xl font-semibold">You're on the list.</p>
              <p className="mt-2 text-sm text-slate-300">We'll reach out to {form.email} within 48 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="early-access-form">
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5">Name</label>
                <input required maxLength={120} className={inputCls}
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.15)" }}
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="early-access-name-input" />
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5">Work email</label>
                <input type="email" required maxLength={254} className={inputCls}
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.15)" }}
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="early-access-email-input" />
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5">Company</label>
                <input maxLength={200} className={inputCls}
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.15)" }}
                  value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="early-access-company-input" />
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5">Industry</label>
                <select className={inputCls}
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.15)" }}
                  value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  data-testid="early-access-industry-select">
                  <option value="" style={{ background: NAVY }}>Choose industry</option>
                  {industries.map((i) => <option key={i} value={i} style={{ background: NAVY }}>{i}</option>)}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5">Team size</label>
                <select required className={inputCls}
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.15)" }}
                  value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                  data-testid="early-access-company-size-select">
                  <option value="" style={{ background: NAVY }}>Select team size</option>
                  {sizes.map((s) => <option key={s} value={s} style={{ background: NAVY }}>{s} employees</option>)}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5">Biggest challenge (optional)</label>
                <textarea rows={1} maxLength={2000} className={inputCls + " h-11 py-2.5 resize-none"}
                  style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.15)" }}
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="early-access-message-input" />
              </div>
              <div className="sm:col-span-2 mt-2">
                <button type="submit" disabled={submitting}
                  className="w-full h-12 rounded-full text-sm font-medium text-white transition-transform hover:scale-[1.01]"
                  style={{ background: BLUE }} data-testid="early-access-submit-button">
                  {submitting ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Joining…</span>
                              : <>Request early access <ArrowRight className="w-4 h-4 inline ml-1" /></>}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-8 text-center text-xs text-white/50 uppercase tracking-wider">
          Used by teams at <span className="text-white/80">Technology Companies</span> · <span className="text-white/80">Healthcare Groups</span> · <span className="text-white/80">Professional Services Firms</span>
        </p>
      </div>
    </section>
  );
}
